import React, { useEffect } from 'react';
import { Form, App } from 'antd';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { PLAN_UPDATE } from '../../../graphql/mutations/plans';
import { PLAN } from '../../../graphql/queries/plans';
import { PlanForm } from '../../../components/Plans/PlanForm';
import CardLayout from '../../../layouts/CardLayout';
import { Loader } from '../../../components/UI/Loader';
import { IPlanPhoto, PlanFormValues, IPlanPhotoForm } from '../../../types/plans';

export const PlanEdit: React.FC = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  const {
    loading: queryLoading,
    refetch,
    data,
  } = useQuery(PLAN, {
    variables: { id },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      form.setFieldsValue({
        ...data.plan,
        photos:
          data.plan.photos?.map((photo: IPlanPhoto) => ({
            url: photo.original,
            status: 'done',
            selected: photo.isPublish,
            isPrimary: !!photo.isPrimary,
            size: photo.size || 0,
            type: photo.type || '',
            compressed: photo.compressed,
            tags: photo.tags || [],
          })) || [],
        image: data.plan.image ? [{ url: data.plan.image }] : [],
      });
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const [updatePlan, { loading: updateLoading }] = useMutation(PLAN_UPDATE, {
    onCompleted: () => {
      message.success('Plan successfully updated');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onFinish = async (values: PlanFormValues) => {
    try {
      const photos = values.photos?.map((photo: IPlanPhotoForm) => ({
        original: photo.url,
        isPublish: !!photo.selected,
        isPrimary: !!photo.isPrimary,
        size: photo.size || 0,
        type: photo.type || '',
        tags: photo.tags || [],
        compressed: photo.compressed && {
          url: photo.compressed.url,
          size: photo.compressed.size || 0,
          type: photo.compressed.type || '',
          watermarked: photo.compressed.watermarked || false,
        },
      }));
      const image = (Array.isArray(values.image) && values.image[0]?.url) || '';
      const updateData = {
        id,
        ...values,
        planCost: values.planCost ? Number(values.planCost) : 0,
        fixPrice: values.fixPrice ? Number(values.fixPrice) : 0,
        photos,
        image,
      };
      if (!updateData.preApproved) {
        updateData.preApprovedArea = {
          states: [],
          counties: [],
          cities: [],
        };
      }
      await updatePlan({
        variables: updateData,
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (queryLoading) {
    return <Loader />;
  }

  return (
    <CardLayout>
      <PlanForm
        form={form}
        onFinish={onFinish}
        loading={updateLoading}
        submitText="Update Plan"
        plan={data?.plan}
      />
    </CardLayout>
  );
};
